import { useEffect, useRef, useState } from "react";
import { FiPause, FiPlay } from "react-icons/fi";
import WaveSurfer from "wavesurfer.js";
import { normalizeAudioFile, trimAudioFile } from "../../../utils/audioTrim";

interface WaveformTrimmerProps {
  file: File;
  onConfirm: (trimmedFile: File) => void;
  onCancel: () => void;
}

function fmtTime(s: number): string {
  return `${s.toFixed(2)}s`;
}

// Keyboard nudge: 0.5% of duration per key press
const STEP = 0.005;

export function WaveformTrimmer({ file, onConfirm, onCancel }: WaveformTrimmerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const previewTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const [duration, setDuration] = useState(0);
  const [startPct, setStartPct] = useState(0);
  const [endPct, setEndPct] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);
  const [activeDrag, setActiveDrag] = useState<"start" | "end" | null>(null);
  const [normalizedFile, setNormalizedFile] = useState<File | null>(null);

  // Refs keep drag handlers fresh without stale closures
  const startPctRef = useRef(startPct);
  const endPctRef = useRef(endPct);
  startPctRef.current = startPct;
  endPctRef.current = endPct;

  const startTime = startPct * duration;
  const endTime = endPct * duration;

  // pre-normalize the full file so preview playback is at the same loudness as the upload.
  useEffect(() => {
    let cancelled = false;
    setNormalizedFile(null);
    normalizeAudioFile(file).then((f) => {
      if (!cancelled) setNormalizedFile(f);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  useEffect(() => {
    if (!containerRef.current || !normalizedFile) return;
    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#bfdbfe",
      progressColor: "#3b82f6",
      height: 88,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      interact: false,
    });
    ws.loadBlob(normalizedFile);
    ws.on("ready", () => setDuration(ws.getDuration()));
    ws.on("finish", () => setIsPlaying(false));
    wsRef.current = ws;
    return () => {
      if (previewTimer.current) clearInterval(previewTimer.current);
      ws.destroy();
    };
  }, [normalizedFile]);

  // Click anywhere on waveform → snap nearest handle to that position
  const handleWaveClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (activeDrag) return; // ignore click when finishing a drag
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const distStart = Math.abs(pct - startPctRef.current);
    const distEnd = Math.abs(pct - endPctRef.current);
    if (distStart <= distEnd) {
      setStartPct(Math.min(pct, endPctRef.current - 0.02));
    } else {
      setEndPct(Math.max(pct, startPctRef.current + 0.02));
    }
  };

  const makeDragHandler = (which: "start" | "end") => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setActiveDrag(which);

    const onMove = (ev: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const raw = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      if (which === "start") {
        setStartPct(Math.min(raw, endPctRef.current - 0.02));
      } else {
        setEndPct(Math.max(raw, startPctRef.current + 0.02));
      }
    };

    const onUp = () => {
      setActiveDrag(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Arrow keys nudge the focused handle
  const makeKeyHandler = (which: "start" | "end") => (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const delta = e.key === "ArrowLeft" ? -STEP : STEP;
    if (which === "start") {
      setStartPct((p) => Math.max(0, Math.min(p + delta, endPctRef.current - 0.02)));
    } else {
      setEndPct((p) => Math.min(1, Math.max(p + delta, startPctRef.current + 0.02)));
    }
  };

  const handlePlayPreview = () => {
    const ws = wsRef.current;
    if (!ws || duration === 0) return;
    if (isPlaying) {
      ws.pause();
      setIsPlaying(false);
      if (previewTimer.current) clearInterval(previewTimer.current);
      return;
    }
    ws.setTime(startTime);
    ws.play();
    setIsPlaying(true);
    previewTimer.current = setInterval(() => {
      if ((ws.getCurrentTime() ?? 0) >= endTime) {
        ws.pause();
        setIsPlaying(false);
        if (previewTimer.current) clearInterval(previewTimer.current);
      }
    }, 40);
  };

  const handleConfirm = async () => {
    setIsTrimming(true);
    try {
      const trimmed = await trimAudioFile(file, startPct, endPct);
      onConfirm(trimmed);
    } catch (err) {
      console.error("Trim failed:", err);
    } finally {
      setIsTrimming(false);
    }
  };

  const selectionWidth = `${(endPct - startPct) * 100}%`;
  const selectionLeft = `${startPct * 100}%`;

  return (
    <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">Trim Audio</span>
        {duration > 0 && (
          <span className="rounded bg-blue-50 px-2 py-0.5 font-mono text-xs text-blue-600">
            {fmtTime(endTime - startTime)} of {fmtTime(duration)}
          </span>
        )}
      </div>

      {/* Waveform + handles */}
      <div
        className="relative cursor-crosshair overflow-hidden rounded-md bg-gray-50 select-none"
        ref={containerRef}
        onClick={handleWaveClick}
      >
        {/* Loading overlay */}
        {(!normalizedFile || duration === 0) && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-gray-50">
            <span className="text-xs text-gray-400">
              {!normalizedFile ? "Analyzing audio…" : "Loading waveform…"}
            </span>
          </div>
        )}

        {/* Dim: discarded left region */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 bg-white/70"
          style={{ width: `${startPct * 100}%` }}
        />
        {/* Dim: discarded right region */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 bg-white/70"
          style={{ width: `${(1 - endPct) * 100}%` }}
        />
        {/* Selection border: top + bottom lines */}
        <div
          className="pointer-events-none absolute top-0 z-10 h-0.5 bg-blue-500"
          style={{ left: selectionLeft, width: selectionWidth }}
        />
        <div
          className="pointer-events-none absolute bottom-0 z-10 h-0.5 bg-blue-500"
          style={{ left: selectionLeft, width: selectionWidth }}
        />

        {/* Start handle */}
        <div
          tabIndex={0}
          role="slider"
          aria-label="Trim start"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={Math.round(startTime * 100) / 100}
          className="absolute inset-y-0 z-20 flex cursor-ew-resize items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          style={{ left: `calc(${startPct * 100}% - 12px)`, width: 24 }}
          onPointerDown={makeDragHandler("start")}
          onKeyDown={makeKeyHandler("start")}
        >
          {/* Vertical line */}
          <div className="h-full w-0.5 bg-blue-600" />
          {/* Top grip tab */}
          <div className="absolute top-0 left-1/2 flex h-5 w-5 -translate-x-1/2 flex-col items-center justify-center gap-0.5 rounded-b-sm bg-blue-600 shadow-sm">
            <div className="h-px w-2.5 rounded-full bg-white/80" />
            <div className="h-px w-2.5 rounded-full bg-white/80" />
            <div className="h-px w-2.5 rounded-full bg-white/80" />
          </div>
          {/* Time badge at bottom */}
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-blue-600 px-1.5 py-0.5 font-mono text-[9px] text-white shadow">
            {fmtTime(startTime)}
          </div>
        </div>

        {/* End handle */}
        <div
          tabIndex={0}
          role="slider"
          aria-label="Trim end"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={Math.round(endTime * 100) / 100}
          className="absolute inset-y-0 z-20 flex cursor-ew-resize items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          style={{ left: `calc(${endPct * 100}% - 12px)`, width: 24 }}
          onPointerDown={makeDragHandler("end")}
          onKeyDown={makeKeyHandler("end")}
        >
          <div className="h-full w-0.5 bg-blue-600" />
          <div className="absolute top-0 left-1/2 flex h-5 w-5 -translate-x-1/2 flex-col items-center justify-center gap-0.5 rounded-b-sm bg-blue-600 shadow-sm">
            <div className="h-px w-2.5 rounded-full bg-white/80" />
            <div className="h-px w-2.5 rounded-full bg-white/80" />
            <div className="h-px w-2.5 rounded-full bg-white/80" />
          </div>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-blue-600 px-1.5 py-0.5 font-mono text-[9px] text-white shadow">
            {fmtTime(endTime)}
          </div>
        </div>
      </div>

      {/* Timeline ticks */}
      {duration > 0 && (
        <div className="flex justify-between px-0.5 font-mono text-[10px] text-gray-400">
          <span>0s</span>
          <span>{fmtTime(duration / 2)}</span>
          <span>{fmtTime(duration)}</span>
        </div>
      )}

      {/* Hint */}
      <p className="text-[11px] text-gray-400">
        Drag handles or click waveform · Arrow keys for fine control
      </p>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handlePlayPreview}
          disabled={duration === 0}
          className="flex items-center gap-1.5 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {isPlaying ? <FiPause size={12} /> : <FiPlay size={12} />}
          {isPlaying ? "Pause" : "Preview"}
        </button>

        <div className="flex-1" />

        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          Skip Trim
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isTrimming || duration === 0}
          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {isTrimming ? "Trimming…" : "Apply Trim"}
        </button>
      </div>
    </div>
  );
}
